/**
 * PhotoEditor — quick annotate-before-send editor.
 *
 * Loads the chosen image into a canvas, lets the user scribble on top
 * with a coloured brush (freehand, touch + mouse), undo the last
 * stroke, or clear everything. Result is exported back as a File so
 * the caller can upload it just like the raw pick.
 *
 * Optimised for on-site scaffolding notes: e.g. circle a defect on a
 * photo before sending it in chat.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Undo2, Trash2, Send, Palette } from "lucide-react";

const COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#0f172a", "#ffffff"];
const BRUSH_SIZES = [4, 8, 14, 22];

// Maximum canvas pixel dimension — keeps memory sane on older phones.
const MAX_DIM = 2000;

export default function PhotoEditor({ file, onCancel, onDone, labels = {} }) {
  const L = {
    title: labels.title || "Rediger bilde",
    send: labels.send || "Send",
    skip: labels.skip || "Send uten redigering",
    undo: labels.undo || "Angre",
    clear: labels.clear || "Nullstill",
    cancel: labels.cancel || "Avbryt",
    color: labels.color || "Farge",
    size: labels.size || "Størrelse",
  };
  const canvasRef = useRef(null);
  const overlayRef = useRef(null); // For drawing (transparent, above image)
  const containerRef = useRef(null);
  const loadedImgRef = useRef(null); // decoded HTMLImageElement, kept for redraws
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(BRUSH_SIZES[1]);
  const [strokes, setStrokes] = useState([]); // [{color, size, points:[{x,y}]}]
  const drawingRef = useRef(null); // current stroke
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [dispSize, setDispSize] = useState({ w: 0, h: 0 });
  const [loadError, setLoadError] = useState(false);

  // 1. Decode the incoming File → HTMLImageElement, capture size.
  //    We keep the decoded Image around so a later render (when
  //    canvasRef finally attaches on slow devices) can still draw.
  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;
    // Guard against formats HTMLImageElement can't decode
    // (Samsung "Motion Photo" HEIC/HEIF sometimes shows up here).
    const badType = /^image\/(heic|heif|avif)/i.test(file.type || "");
    if (badType) {
      // Skip the editor for unsupported types — the caller uploads
      // the raw bytes so the photo still gets through.
      setLoadError(true);
      onDone?.(file);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (cancelled) return;
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      if (!w || !h) {
        setLoadError(true);
        onDone?.(file);
        return;
      }
      const scale = Math.min(1, MAX_DIM / Math.max(w, h));
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      loadedImgRef.current = img;
      setImgSize({ w, h });
    };
    img.onerror = () => {
      if (cancelled) return;
      // Broken/unsupported bytes → just upload as-is.
      setLoadError(true);
      onDone?.(file);
      URL.revokeObjectURL(url);
    };
    img.src = url;
    return () => {
      cancelled = true;
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    };
  }, [file]);

  // 2. Once BOTH the decoded image and the canvas ref exist, paint.
  //    This split fixes the "black screen" on Android where the canvas
  //    ref wasn't ready in the same microtask that fired img.onload.
  useEffect(() => {
    if (!imgSize.w || !imgSize.h) return;
    const img = loadedImgRef.current;
    const c = canvasRef.current;
    const o = overlayRef.current;
    if (!img || !c) return;
    c.width = imgSize.w;
    c.height = imgSize.h;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, imgSize.w, imgSize.h);
    if (o) { o.width = imgSize.w; o.height = imgSize.h; }
  }, [imgSize]);

  // Fit the canvas visually inside the container while keeping aspect.
  useEffect(() => {
    if (!imgSize.w || !imgSize.h) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const compute = () => {
      const box = el.getBoundingClientRect();
      // Fall back to viewport dims when the container hasn't laid out
      // yet — happens on the first mount on some Android browsers.
      const availW = box.width > 0 ? box.width : (window.innerWidth - 16);
      const availH = box.height > 0 ? box.height : (window.innerHeight * 0.55);
      const s = Math.min(availW / imgSize.w, availH / imgSize.h);
      setDispSize({ w: Math.floor(imgSize.w * s), h: Math.floor(imgSize.h * s) });
    };
    compute();
    window.addEventListener("resize", compute);
    // Retry once after a tick — first getBoundingClientRect on Android
    // Chrome sometimes returns 0×0 before the flex parent is measured.
    const retry = setTimeout(compute, 60);
    return () => {
      window.removeEventListener("resize", compute);
      clearTimeout(retry);
    };
  }, [imgSize]);

  // Redraw all strokes on the overlay whenever `strokes` changes.
  useEffect(() => {
    const o = overlayRef.current;
    if (!o) return;
    const ctx = o.getContext("2d");
    ctx.clearRect(0, 0, o.width, o.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes) {
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.size;
      ctx.beginPath();
      s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    }
  }, [strokes]);

  const getPoint = (ev) => {
    const o = overlayRef.current;
    const rect = o.getBoundingClientRect();
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
    const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
    // Map from displayed pixels to internal canvas pixels.
    const scaleX = o.width / rect.width;
    const scaleY = o.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (ev) => {
    ev.preventDefault();
    drawingRef.current = { color, size, points: [getPoint(ev)] };
  };
  const moveDraw = (ev) => {
    if (!drawingRef.current) return;
    ev.preventDefault();
    drawingRef.current.points.push(getPoint(ev));
    // Live-draw the newest segment straight to the overlay so it
    // feels responsive without redrawing the whole array.
    const o = overlayRef.current;
    const ctx = o.getContext("2d");
    ctx.strokeStyle = drawingRef.current.color;
    ctx.lineWidth = drawingRef.current.size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const pts = drawingRef.current.points;
    const a = pts[pts.length - 2];
    const b = pts[pts.length - 1];
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    setStrokes((cur) => [...cur, drawingRef.current]);
    drawingRef.current = null;
  };

  const undo = () => setStrokes((cur) => cur.slice(0, -1));
  const clear = () => setStrokes([]);

  const commit = async (withEdits = true) => {
    const c = canvasRef.current;
    if (!c) return;
    if (!withEdits) {
      // Send the original bytes as-is.
      onDone(file);
      return;
    }
    // Merge overlay onto a fresh canvas so we produce a single flat image.
    const out = document.createElement("canvas");
    out.width = c.width;
    out.height = c.height;
    const octx = out.getContext("2d");
    octx.drawImage(c, 0, 0);
    octx.drawImage(overlayRef.current, 0, 0);
    const blob = await new Promise((resolve) =>
      out.toBlob((b) => resolve(b), "image/jpeg", 0.9)
    );
    if (!blob) return;
    const named = new File([blob], (file.name || `edit-${Date.now()}.jpg`).replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
    onDone(named);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/95 flex flex-col" data-testid="photo-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-700 flex-shrink-0" style={{ color: "#fff", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
        <button onClick={onCancel} className="w-9 h-9 rounded-lg hover:bg-white/10 flex items-center justify-center" data-testid="photo-editor-cancel" aria-label={L.cancel} style={{ color: "#fff" }}>
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-black" style={{ color: "#fff" }}>{L.title}</div>
        <div className="w-9" />
      </div>
      {/* Canvas stage */}
      <div ref={containerRef} className="flex-1 min-h-0 flex items-center justify-center overflow-hidden p-2">
        <div className="relative touch-none" style={{ width: dispSize.w || "auto", height: dispSize.h || "auto" }}>
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full rounded-lg shadow-2xl bg-slate-800"
          />
          <canvas
            ref={overlayRef}
            className="absolute inset-0 w-full h-full rounded-lg cursor-crosshair"
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
            data-testid="photo-editor-canvas"
          />
        </div>
      </div>
      {/* Tools */}
      <div className="border-t border-slate-700 px-3 py-2 flex flex-col gap-2 flex-shrink-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}>
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-slate-300 flex-shrink-0" />
          <div className="flex gap-1 flex-1 overflow-x-auto">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-full border-2 flex-shrink-0 ${color === c ? "border-white scale-110" : "border-slate-500"}`}
                style={{ backgroundColor: c }}
                data-testid={`photo-color-${c}`}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-black flex-shrink-0 w-4">S</span>
          <div className="flex gap-1 flex-1">
            {BRUSH_SIZES.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${size === s ? "bg-white/20" : ""}`}
                data-testid={`photo-size-${s}`}
                aria-label={`Brush ${s}px`}
              >
                <span className="rounded-full block" style={{ width: s, height: s, backgroundColor: color }} />
              </button>
            ))}
          </div>
          <button
            onClick={undo}
            disabled={strokes.length === 0}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-xs font-bold"
            style={{ color: "#fff" }}
            data-testid="photo-undo"
          >
            <Undo2 className="w-3.5 h-3.5" /> {L.undo}
          </button>
          <button
            onClick={clear}
            disabled={strokes.length === 0}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-40 text-xs font-bold"
            style={{ color: "#fff" }}
            data-testid="photo-clear"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => commit(false)}
            className="flex-1 py-2.5 rounded-lg border border-slate-500 text-xs font-black"
            style={{ color: "#fff" }}
            data-testid="photo-send-original"
          >
            {L.skip}
          </button>
          <button
            onClick={() => commit(true)}
            className="flex-[1.5] inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#0084ff] hover:bg-[#0074e1] text-sm font-black"
            style={{ color: "#fff" }}
            data-testid="photo-send-edited"
          >
            <Send className="w-4 h-4" /> {L.send}
          </button>
        </div>
      </div>
    </div>
  );
}
